import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { COURSES } from "@/data/courses";
import { SITE, telLink, waLink } from "@/data/site";
import { Button } from "@/components/ui/button";

export default function Header() {
    const [open, setOpen] = useState(false);
    const [coursesOpen, setCoursesOpen] = useState(false);

    return (
        <header
            data-testid="site-header"
            className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-black/10"
        >
            <div className="hidden md:block bg-brand-navy text-white text-xs">
                <div className="container-x flex items-center justify-between h-9">
                    <div className="tracking-[0.18em] uppercase font-medium opacity-90">
                        Admissions Open 2026 · Free Demo Class · Pathankot, Punjab
                    </div>
                    <div className="flex items-center gap-5">
                        <a
                            href={telLink}
                            data-testid="topbar-phone"
                            className="hover:text-brand-gold transition-colors flex items-center gap-1.5"
                        >
                            <Phone className="w-3.5 h-3.5" /> {SITE.phone}
                        </a>
                        <a
                            href={waLink()}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="topbar-whatsapp"
                            className="hover:text-brand-gold transition-colors"
                        >
                            WhatsApp Us
                        </a>
                    </div>
                </div>
            </div>

            <div className="container-x flex items-center justify-between h-16 md:h-20">
                <Link
                    to="/"
                    data-testid="logo-link"
                    className="flex items-center gap-2.5"
                >
                    <div className="w-10 h-10 bg-brand-navy text-white grid place-items-center font-heading font-black text-lg">
                        D
                    </div>
                    <div className="leading-tight">
                        <div className="font-heading font-extrabold text-lg tracking-tight text-brand-navy">
                            Dacron Classes
                        </div>
                        <div className="text-[10px] tracking-[0.22em] uppercase text-brand-sub">
                            Pathankot · Punjab
                        </div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-8 text-sm font-medium">
                    <NavLink
                        to="/"
                        end
                        data-testid="nav-home"
                        className={({ isActive }) =>
                            `hover:text-brand-crimson transition-colors ${
                                isActive ? "text-brand-crimson" : "text-brand-navy"
                            }`
                        }
                    >
                        Home
                    </NavLink>

                    <div
                        className="relative"
                        onMouseEnter={() => setCoursesOpen(true)}
                        onMouseLeave={() => setCoursesOpen(false)}
                    >
                        <button
                            data-testid="nav-courses-trigger"
                            className="flex items-center gap-1 text-brand-navy hover:text-brand-crimson transition-colors"
                        >
                            Courses <ChevronDown className="w-4 h-4" />
                        </button>
                        {coursesOpen && (
                            <div
                                data-testid="nav-courses-menu"
                                className="absolute top-full left-1/2 -translate-x-1/2 mt-0 pt-3 w-[640px]"
                            >
                                <div className="bg-white border border-black/10 shadow-xl p-6 grid grid-cols-2 gap-x-8 gap-y-3">
                                    {COURSES.map((c) => (
                                        <Link
                                            key={c.slug}
                                            to={`/${c.slug}`}
                                            data-testid={`nav-course-${c.slug}`}
                                            className="group flex items-start gap-3 py-1.5 hover:bg-brand-surface px-2 -mx-2 transition-colors"
                                        >
                                            <span className="overline text-brand-crimson mt-1 w-6">
                                                {c.category[0]}
                                            </span>
                                            <div>
                                                <div className="font-heading font-semibold text-brand-navy group-hover:text-brand-crimson">
                                                    {c.short}
                                                </div>
                                                <div className="text-xs text-brand-sub">
                                                    in Pathankot
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <a
                        href="#why-us"
                        data-testid="nav-why-us"
                        className="text-brand-navy hover:text-brand-crimson transition-colors"
                    >
                        Why Us
                    </a>
                    <a
                        href="#testimonials"
                        data-testid="nav-testimonials"
                        className="text-brand-navy hover:text-brand-crimson transition-colors"
                    >
                        Results
                    </a>
                    <a
                        href="#contact"
                        data-testid="nav-contact"
                        className="text-brand-navy hover:text-brand-crimson transition-colors"
                    >
                        Contact
                    </a>
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    <Button
                        asChild
                        data-testid="header-cta-call"
                        variant="outline"
                        className="rounded-none border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white"
                    >
                        <a href={telLink}>
                            <Phone className="w-4 h-4 mr-1.5" /> Call Now
                        </a>
                    </Button>
                    <Button
                        asChild
                        data-testid="header-cta-enquire"
                        className="rounded-none bg-brand-crimson hover:bg-brand-crimsonHover text-white font-semibold"
                    >
                        <a href="#enquiry">Book Free Demo</a>
                    </Button>
                </div>

                <button
                    data-testid="mobile-menu-trigger"
                    className="lg:hidden p-2"
                    onClick={() => setOpen(!open)}
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden bg-white border-t border-black/10"
                >
                    <div className="container-x py-4 flex flex-col gap-1">
                        <Link
                            to="/"
                            data-testid="mnav-home"
                            onClick={() => setOpen(false)}
                            className="py-2 font-medium text-brand-navy"
                        >
                            Home
                        </Link>
                        <div className="overline text-brand-sub mt-3 mb-1">Courses</div>
                        {COURSES.map((c) => (
                            <Link
                                key={c.slug}
                                to={`/${c.slug}`}
                                onClick={() => setOpen(false)}
                                data-testid={`mnav-course-${c.slug}`}
                                className="py-2 text-sm text-brand-navy border-b border-black/5"
                            >
                                {c.short} <span className="text-brand-sub">· Pathankot</span>
                            </Link>
                        ))}
                        <a
                            href={telLink}
                            data-testid="mnav-call"
                            className="py-3 mt-3 bg-brand-navy text-white text-center font-semibold"
                        >
                            <Phone className="inline w-4 h-4 mr-1.5" />
                            Call {SITE.phone}
                        </a>
                        <a
                            href="#enquiry"
                            onClick={() => setOpen(false)}
                            data-testid="mnav-enquire"
                            className="py-3 bg-brand-crimson text-white text-center font-semibold"
                        >
                            Book Free Demo Class
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
